import React, { useEffect, useRef, useState } from 'react';
import { Modal, Tabs, Tab } from 'react-bootstrap';
import Pagination from "react-js-pagination";
import LoadingWheel from '../loadingwheel';
import SensorService from '../../services/sensorService';
import MlAlertLogsTab, { flattenComponents } from '../MlAlertLogsTab';
import MlAlertChartModal, { healthChartToAlertLog } from '../MlAlertChartModal';
import "../AlertLogsModal/alertLogsModal.css"
import '../maintenance-checklist/maintenance-checklist.css';
import moment from 'moment'
import constants from '../../config/constants';
import authStore from '../../store/authStore';
import Permissions from '../../config/permssions';
import { useHistory } from "react-router-dom";
import Routes from '../../routing/routes';

const maxLimit = 16
const {
  view_only_partial_asset_sensorbox_details,
  create_edit_issue_tracker
} = Permissions

interface Props {
  show: boolean;
  onClose: () => void;
  sensorBoxName?: string;
  asset_id?: string;
  sensorBox: any,
  showLogDataModal: (logData: any) => void;
  showInvalidRangeAlertModal:(logData: any) => void;
  lastTab:string;
  onTabChange?: (tab: string) => void;
}

const AssetLogsModal: React.FC<Props> = ({ show, sensorBoxName, asset_id, sensorBox, onClose,
  showLogDataModal,showInvalidRangeAlertModal ,lastTab, onTabChange}) => {

  const [loading, setLoading] = useState(true)
  const [assetLogs, setAssetLogs] = useState<Array<any>>([])
  const [currentAlertLogPage, setCurrentAlertLogPage] = useState(1)
  const [assetLogsTotal, setAssetlogsTotal] = useState(0)
  const [activeTab, setActiveTab] = useState(lastTab || 'status')

  const [existingTicketModal, setExistingTicketModal] = useState(false)
  const [existingTicket, setExistingTicket] = useState<any>(null)
  const [currentAlertLog, setCurrentAlertLog] = useState<any>(null)
  const [chartAlertLog, setChartAlertLog] = useState<any>(null)
  const [showChartModal, setShowChartModal] = useState(false)
  const [chartLoading, setChartLoading] = useState(false)
  const chartRequest = useRef(0)

  const history = useHistory();

  const canViewPredictionAlerts = authStore?.isOrgAdmin();

  useEffect(() => {
    const nextTab = lastTab || 'status'
    if (nextTab === 'predictive' && !canViewPredictionAlerts) {
      setActiveTab('status')
      return
    }
    setActiveTab(nextTab)
  }, [lastTab, canViewPredictionAlerts]);

  useEffect(() => {
    getAssetLogs()
  }, [sensorBox, asset_id]);

  const handleTabSelect = (key: string | null) => {
    const nextTab = key || 'status'
    setActiveTab(nextTab)
    onTabChange?.(nextTab)
  }

  const getAssetLogs = async (pageNumber: number = 0) => {
    if (asset_id) {
      setLoading(true)
      pageNumber = pageNumber === 0 ? currentAlertLogPage : pageNumber;

      let response;
      if (sensorBox?.category === "generator") {
        response = await SensorService.getAlertLogs(asset_id, pageNumber, maxLimit)
      } else {
        response = await SensorService.getAssetLogs(asset_id, pageNumber, maxLimit)
      }
      setAssetLogs(response.data.logs)
      setCurrentAlertLogPage(pageNumber)
      if (response.data.total) {
        setAssetlogsTotal(response.data.total)
      } else {
        setAssetlogsTotal(0)
      }
      setLoading(false)
    }
  }

  const demoCheck = (authStore?.currentUser?.active_role?.role_type === -1);
  const canCreateEditTickets = authStore?.currentUser?.access?.includes(create_edit_issue_tracker);

  const algorithmDescriptionCheck = (item: any) => {
    return item?.description
  }

  const recipientList = (emails: any) => {
    const external = (emails?.external || []).filter(Boolean)
    if (external.length === 0) {
      return 'No External Recipients'
    }
    return external.toString().replace(/,/g, ', ')
  }

  const checkExistingTicket = async (alertLog: any) => {
    let code = "";
    if (sensorBox?.category === "generator") {
      code = alertLog.diagnostics_code
    } else {
      code = alertLog.trigger_code?.code
    }
    
    try {
      const response = await SensorService.checkExistingTicket(asset_id || '', code);
      return response;
    } catch (error) {
      console.error('Error checking existing ticket:', error);
      return { exists: false };
    }
  }

  const handleIssueTrackerClick = async (alertLog: any) => {
    const existingTicketData = await checkExistingTicket(alertLog);
    
    if (existingTicketData.exists) {
      setExistingTicket(existingTicketData.ticket);
      setCurrentAlertLog(alertLog);
      setExistingTicketModal(true);
    } else {
      showIssueCreationPage(alertLog);
    }
  }

  const showIssueCreationPage = (alertLog: any) => {
    let orgid = sensorBox.org_id

    let params = `?group_id=${asset_id}&status=${sensorBox?.status}`

    if(authStore?.isSuperAdmin || (authStore?.isOrgAdmin() && authStore.currentUser.is_default)){
        params += `&org_id=${orgid}`
    }

    let desc = "";
    let color;
    let code = "";
    if (sensorBox?.category === "generator") {
      desc = alertLog.description
      color = constants.statusColor.get(String(alertLog.status))
      code = alertLog.diagnostics_code
    } else {
      let triggerCode = alertLog.trigger_code
      desc = algorithmDescriptionCheck(triggerCode)
      color = triggerCode?.color
      code = triggerCode?.code
    }
    params += `&alert_description=${desc}`
    params += `&alert_code=${code.replace(/#.*$/, '')}`
    params += `&alert_color=${color}`
    
    if (alertLog._id) {
      params += `&alert_log_id=${alertLog._id}`
    }

    history.push({
      pathname: Routes.issuetrackerdetails,
      search: params,
    });
  }

  const handleOpenExistingTicket = () => {
    if (existingTicket) {
      let params = `?ticket_id=${existingTicket.id}`;
      if (authStore?.isSuperAdmin || (authStore?.isOrgAdmin() && authStore.currentUser.is_default)) {
        params += `&org_id=${sensorBox.org_id}`;
      }
      history.push({
        pathname: Routes.issuetrackerdetails,
        search: params,
      });
    }
    setExistingTicketModal(false);
  }

  const handleCloseExistingTicket = () => {
    setExistingTicketModal(false);
  }

  const handleOpenNewTicket = () => {
    if (currentAlertLog) {
      showIssueCreationPage(currentAlertLog);
      setExistingTicketModal(false);
    }
  }

  const chartTitleName = sensorBox?.alias || sensorBox?.name || sensorBoxName

  const openHealthChart = async (item: any, code: string, description: string, sensorValues?: any) => {
    const requestId = ++chartRequest.current
    const request = { logId: item._id, code, description }
    const baseLog = {
      chartTitle: chartTitleName && code ? `${chartTitleName} - ${code}` : (code || chartTitleName),
      healthChartRequest: request,
      sensorValues: sensorValues || null,
      trigger_code: { code, description },
      ml: { chart: { points: [], marker_ms: item.timestamp } }
    }
    setChartAlertLog(baseLog)
    setChartLoading(true)
    setShowChartModal(true)
    try {
      const chart = await SensorService.getHealthAlertChart(item._id, code, undefined, description)
      if (requestId !== chartRequest.current) return
      setChartAlertLog({
        ...healthChartToAlertLog(chart, chartTitleName, request),
        sensorValues: sensorValues || null,
      })
    } catch (error) {
      if (requestId !== chartRequest.current) return
      setChartAlertLog(baseLog)
    } finally {
      if (requestId === chartRequest.current) setChartLoading(false)
    }
  }

  const closeChartModal = () => {
    chartRequest.current += 1
    setShowChartModal(false)
    setChartAlertLog(null)
    setChartLoading(false)
  }

  let failureModeStyleWidth = '54%'
  if(sensorBox.device_type === 'lockrouter') failureModeStyleWidth = '68%'

  return (
    <>
    <Modal
      show={show && !showChartModal}
      onHide={onClose}
      size="lg"
    >
      <Modal.Header closeButton>
      <div>{sensorBoxName} {sensorBox.alias && <span className='pl-2'>( {sensorBox.alias} )</span> }</div>
      </Modal.Header>
      <Modal.Body>
        <Tabs variant="pills" className="checklist-tab mb-3" activeKey={activeTab} onSelect={handleTabSelect}>
          <Tab eventKey="status" title="Health Alerts">
            <div>
            <div className="table-wrap">
              {loading ?
                <LoadingWheel />
                :
                <table className={assetLogs.length > 0 ? "table custom-table table-style-logs text-custom-size" : "table custom-table table-style-no-logs "} style={{ tableLayout: 'fixed' }}>
                  {assetLogs.length > 0 ? 
                    <>
                      <thead className="table-header-bg">
                        <tr>
                          <th style={{ width: '3%' }} scope="col">#</th>
                          <th style={{ width: '6%' }} className="text-center" scope="col">Status</th>
                          {<th style={{ width: '13%' }} className="text-center" scope="col">Diagnostics Codes</th> }
                          <th style={{ width: failureModeStyleWidth }} className="text-center" scope="col">Failure Mode</th>
                          <th style={{ width: '18%' }} className="text-center" scope="col">Alert Mail Sent On</th>
                          {!authStore?.currentUser?.access?.includes(view_only_partial_asset_sensorbox_details) &&
                            <th style={{ width: '30%' }} className="text-center" scope="col">Recipients</th>
                          }
                          {canCreateEditTickets && (
                                <th style={{ width: '10%' }} className="text-center" scope="col">Tickets</th>
                            )}
                        </tr>
                      </thead>
                      <tbody>
                        {assetLogs?.map((item: any, index) => {
                          let result: Record<string, any> = {};
                          let desc = "";
                          let color;
                          let code = "";
                          if (sensorBox?.category === "generator") {
                            desc = item.description
                            color = constants.statusColor.get(String(item.status))
                            code = item.diagnostics_code
                          } else {
                            let triggerCode = item.trigger_code
                            desc = algorithmDescriptionCheck(triggerCode)
                            color = triggerCode?.color
                            code = triggerCode?.code
                          }

                          if (sensorBox?.category === "generator" && item.values) {
                            result = item.values
                          } else if (item.components) {
                            result = flattenComponents(item.components)
                          }

                          const logData = {
                            components: result,
                            highlightKeys: item.trigger_code?.trigger_pre_req || [],
                            alertColor: color,
                            alertCode: code?.replace(/#.*$/, '')
                          }

                          return (
                            <tr  className={index%2 !== 0 ? "background-color click-icon" : "click-icon"} key={index} onClick={() => {
                              const alertCode = code?.replace(/#.*$/, '') || ''
                              if (sensorBox?.category === 'generator' || !item._id) {
                                showLogDataModal(logData)
                                return
                              }
                              openHealthChart(item, alertCode, desc, {
                                values: logData.components,
                                highlightKeys: logData.highlightKeys,
                                alertColor: logData.alertColor,
                                alertCode: logData.alertCode,
                              })
                            }}>
                              <td>{(maxLimit * (currentAlertLogPage - 1)) + index + 1}</td>
                              <td className="text-center"><i className={`fa fa-circle ${color}-status`} aria-hidden="true"></i></td>
                              <td className="text-truncate text-center" title={code}>
                                {code?.replace(/#.*$/, '')}
                              </td>
                              <td className="text-center" title={desc}>{desc}</td>
                              <td className="text-truncate" title={moment(item?.timestamp).format('MM/DD/YYYY, HH:mm:ss')}>{moment(item?.timestamp).format('MM/DD/YYYY, HH:mm:ss')}</td>
                              {!authStore?.currentUser?.access?.includes(view_only_partial_asset_sensorbox_details) &&
                                <td className="text-truncate" title={demoCheck ? `example@gmail.com` : recipientList(item.emails)}>{demoCheck ? `example@gmail.com` : recipientList(item.emails)}</td>
                              }
                              {canCreateEditTickets && (
                                <td className="text-center">
                                    <i
                                        className="fa fa-flag cursor-pointer px-3"
                                        onClick={(e: any) => {
                                        e.stopPropagation();
                                        handleIssueTrackerClick(item);
                                        }}
                                        aria-hidden="true"
                                    ></i>
                                </td>
                              )}
                              
                            </tr>
                          )
                        })}
                      </tbody>
                    </>
                    :
                    <div>
                      <p className='no-logs-text'>No logs found</p>
                    </div>}
                </table>
              }
            </div>
            {assetLogs.length > 0 && !loading &&
            <div className={Math.ceil(assetLogsTotal / maxLimit) > 1 ? "pagination-container-logs" : "pagination-container-no-logs"}>
              {Math.ceil(assetLogsTotal / maxLimit) > 1 ?
                <Pagination
                  hideNavigation={true}
                  activePage={currentAlertLogPage}
                  itemsCountPerPage={maxLimit}
                  totalItemsCount={assetLogsTotal}
                  itemClass={"item-class"}
                  activeClass={"active-class"}
                  pageRangeDisplayed={5}
                  onChange={(pageNumber) => getAssetLogs(pageNumber)}
                />
                : null
              }
            </div>
          }
          </div>
          </Tab>
          {sensorBox?.category !== "generator" && canViewPredictionAlerts && (
            <Tab eventKey="predictive" title="Prediction Alerts">
              <MlAlertLogsTab
                assetId={asset_id}
                sensorBox={sensorBox}
                onShowChart={(alertLog) => {
                  setChartLoading(false);
                  setChartAlertLog(alertLog);
                  setShowChartModal(true);
                }}
              />
            </Tab>
          )}
        </Tabs>
      </Modal.Body>
      <>
        <ExistingTicketModal
          show={existingTicketModal}
          onClose={handleCloseExistingTicket}
          ticket={existingTicket}
          onOpenTicket={handleOpenExistingTicket}
          onOpenNewTicket={handleOpenNewTicket}
        />
      </>
    </Modal>
    <MlAlertChartModal
      show={showChartModal}
      loading={chartLoading}
      onClose={closeChartModal}
      alertLog={chartAlertLog}
      sensorBoxName={sensorBox?.alias || sensorBox?.name || sensorBoxName}
    />
    </>
  )
}

interface ExistingTicketModalProps {
  show: boolean;
  onClose: () => void;
  ticket: any;
  onOpenTicket: () => void;
  onOpenNewTicket: () => void;
}

const ExistingTicketModal: React.FC<ExistingTicketModalProps> = ({ show, onClose, ticket, onOpenTicket, onOpenNewTicket }) => {
  return (
    <Modal show={show} onHide={onClose} size="sm" centered>
      <Modal.Header closeButton>
        <Modal.Title>Ticket: {ticket?.issue_id}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div className="mb-3">
          <strong>Title:</strong> {ticket?.title}
        </div>
        <div className="mb-3">
          <strong>Status:</strong> <span className={`badge badge-${ticket?.status === 'OPEN' ? 'warning' : ticket?.status === 'RESOLVED' ? 'success' : 'secondary'}`}>{ticket?.status}</span>
        </div>
        <div className="mb-3">
          <strong>Last Updated:</strong> {moment(ticket?.updated_on || ticket?.created_on).format('MM/DD/YYYY - HH:mm:ss')}
        </div>
      </Modal.Body>
      <Modal.Footer>
        <button className="btn btn-secondary" onClick={onOpenNewTicket}>
          Open New Ticket
        </button>
        <button className="btn btn-primary" onClick={onOpenTicket}>
          View Ticket
        </button>
      </Modal.Footer>
    </Modal>
  );
};

export default AssetLogsModal;
